import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Pencil, Plus, Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/shared/components/ui/input";
import type {
  FlyExterminatorMaintenanceMonitorViewDTO,
  UpdateMonitorSnapshotConsumableReq,
} from "@/services/backend/qpm/calculations/types/calculation";
import { ConsumableCadence } from "@/services/backend/qpm/types/enums";
import { ConsumableCadenceUtil } from "@/services/backend/qpm/lib/consumable-cadence-util";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs";

export type ServiceConsumablesTableProps = {
  calculationId: string;
  monitorId: string;
  consumables: FlyExterminatorMaintenanceMonitorViewDTO["consumables"];
  onSaveConsumable: (req: UpdateMonitorSnapshotConsumableReq) => Promise<void>;
};

export function ServiceConsumablesTable({
  calculationId,
  monitorId,
  consumables,
  onSaveConsumable,
}: ServiceConsumablesTableProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const byId = useMemo(() => {
    const map = new Map<
      string,
      FlyExterminatorMaintenanceMonitorViewDTO["consumables"][number]
    >();
    consumables.forEach((it) => {
      map.set(it.consumable.id, it);
    });
    return map;
  }, [consumables]);

  const options = useMemo(
    () =>
      [...consumables]
        .map((x) => x.consumable)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [consumables],
  );

  const rowEditing = editingKey !== null;

  const startAdd = () => {
    setIsAdding(true);
    setEditingKey("__new__");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setIsAdding(false);
  };

  const existingIds = useMemo(
    () => new Set(consumables.map((x) => x.consumable.id)),
    [consumables],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="">{t("Material")}</TableHead>
          <TableHead>{t("Eigenschaften")}</TableHead>
          <TableHead className="text-end">{t("Benötigte Menge")}</TableHead>
          <TableHead className="text-end">{t("Kadenz")}</TableHead>
          <TableHead className="text-end">{t("Preis")}</TableHead>
          <TableHead className="text-end">{t("Aktion")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {consumables.map((item) => {
          const key = item.consumable.id;
          const isEdit = editingKey === key;

          if (isEdit) {
            return (
              <ServiceConsumableEditRow
                key={key}
                initial={byId.get(key)}
                isNew={false}
                options={options}
                existingIds={existingIds}
                onCancel={cancelEdit}
                onSaved={cancelEdit}
                onSave={async (override) => {
                  await onSaveConsumable({
                    calculationId,
                    monitorId,
                    ...override,
                  });
                }}
              />
            );
          }

          return (
            <TableRow key={key}>
              <TableCell className="py-1">{item.consumable.name}</TableCell>
              <TableCell className="py-1">{item.note || "—"}</TableCell>
              <TableCell className="py-1 text-end">
                {item.quantityPerDevice ?? 0}
              </TableCell>
              <TableCell className="py-1 text-end">
                {item.cadence
                  ? ConsumableCadenceUtil.toLongString(item.cadence)
                  : "—"}
              </TableCell>
              <TableCell className="py-1 text-end">
                {item.price != null
                  ? item.price.toLocaleString("de-DE", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "—"}
              </TableCell>
              <TableCell className="py-1 text-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-fit p-1"
                        onClick={() => setEditingKey(key)}
                        disabled={rowEditing}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className={tooltipContentClassName}>
                      {t("Bearbeiten")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          );
        })}

        {isAdding && editingKey === "__new__" ? (
          <ServiceConsumableEditRow
            key="__new__"
            initial={undefined}
            isNew
            options={options}
            existingIds={existingIds}
            onCancel={cancelEdit}
            onSaved={cancelEdit}
            onSave={async (override) => {
              await onSaveConsumable({
                calculationId,
                monitorId,
                ...override,
              });
            }}
          />
        ) : null}

        <TableRow>
          <TableCell colSpan={6} className="py-1 text-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-fit p-1"
                    disabled={rowEditing}
                    onClick={startAdd}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className={tooltipContentClassName}>
                  {t("Verbrauchsmaterial hinzufügen")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

type OverrideDraft = {
  consumableId: string;

  quantityPerDevice: number;
  cadence: ConsumableCadence;
  customAmountOfMeasures?: number;

  note: string;
  overrideEffective: boolean;
  overridePrice?: number;
  effectiveOn?: string;
};

function toNum(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

type ServiceConsumableEditRowProps = {
  initial:
    FlyExterminatorMaintenanceMonitorViewDTO["consumables"][number] | undefined;
  isNew: boolean;
  options: FlyExterminatorMaintenanceMonitorViewDTO["consumables"][number]["consumable"][];
  existingIds: Set<string>;
  onCancel: () => void;
  onSaved: () => void;
  onSave: (draft: OverrideDraft) => Promise<void>;
};

function ServiceConsumableEditRow({
  initial,
  isNew,
  options,
  existingIds,
  onCancel,
  onSaved,
  onSave,
}: ServiceConsumableEditRowProps) {
  const [selectedId, setSelectedId] = useState<string>(
    initial?.consumable.id ?? "",
  );

  const [quantityPerDevice, setQuantityPerDevice] = useState<string>(
    initial ? String(initial.quantityPerDevice ?? 0) : "0",
  );
  const [cadence, setCadence] = useState<ConsumableCadence>(
    (initial?.cadence as ConsumableCadence) ??
      ("EVERY_SERVICE" as ConsumableCadence),
  );
  // const [customAmountOfMeasures, setCustomAmountOfMeasures] = useState<string>(
  //   initial?.customAmountOfMeasures != null
  //     ? String(initial.customAmountOfMeasures)
  //     : "",
  // );
  const [price, setPrice] = useState<string>(
    initial?.price != null ? String(initial.price) : "",
  );
  const [note, setNote] = useState<string>(initial?.note ?? "");

  const isDuplicate = isNew && !!selectedId && existingIds.has(selectedId);
  const canSave = !!selectedId && !isDuplicate;

  const renderConsumableCell = () => (
    <select
      className="border-input bg-background h-8 w-full rounded-md border px-2 text-sm"
      value={selectedId}
      onChange={(e) => setSelectedId(e.target.value)}
    >
      <option value="">{t("Bitte auswählen")}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  const onValueChangeCadence = (value: ConsumableCadence) => {
    setCadence(value);
  };

  const handleSave = async () => {
    if (!canSave || !selectedId) return;
    const override: OverrideDraft = {
      consumableId: selectedId,
      quantityPerDevice: toNum(quantityPerDevice),
      cadence,
      customAmountOfMeasures: initial?.customAmountOfMeasures,
      // customAmountOfMeasures.trim().length > 0
      //   ? toNum(customAmountOfMeasures)
      //   : undefined,

      note,
      overrideEffective: price.trim().length > 0,
      overridePrice: price.trim().length > 0 ? toNum(price) : undefined,
      effectiveOn: new Date().toISOString(),
    };

    await onSave(override);

    onSaved();
  };

  return (
    <TableRow>
      <TableCell className="py-1">{renderConsumableCell()}</TableCell>
      <TableCell className="py-1">
        <Input
          className="h-8"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("Notiz")}
        />
      </TableCell>
      <TableCell className="py-1 text-end">
        <Input
          className="h-8 text-right"
          value={quantityPerDevice}
          onChange={(e) => setQuantityPerDevice(e.target.value)}
        />
      </TableCell>
      <TableCell className="py-1 text-end">
        <Select value={cadence} onValueChange={onValueChangeCadence}>
          <SelectTrigger className="w-full max-w-48">
            <SelectValue placeholder={t("Kadenz wählen")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ConsumableCadence.Never}>
                {ConsumableCadenceUtil.toLongString(ConsumableCadence.Never)}
              </SelectItem>
              <SelectItem value={ConsumableCadence.EveryService}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.EveryService,
                )}
              </SelectItem>
              <SelectItem value={ConsumableCadence.Every2ndService}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.Every2ndService,
                )}
              </SelectItem>
              <SelectItem value={ConsumableCadence.OncePerYear}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.OncePerYear,
                )}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="py-1 text-end">
        <Input
          className="h-8 text-right"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="—"
        />
      </TableCell>
      <TableCell className="py-1 text-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="mr-1 h-fit p-1"
                size="sm"
                variant="outline"
                onClick={onCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={tooltipContentClassName}>
              {t("Abbrechen")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                className="h-fit p-1"
                onClick={handleSave}
                disabled={!canSave}
              >
                <Save className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={tooltipContentClassName}>
              {isDuplicate ? t("Bereits hinzugefügt") : t("Speichern")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}
